import React, { useMemo } from 'react';

import { Icon, Image, ScrollView } from '@rbilabs/universal-components';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { BottomSheet } from 'components/bottom-sheet';
import CloseButton from 'components/close-button';
import { InlineModal } from 'components/ui';
import { useIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants/primitives';

import { IconStyled } from '../styled';
import { ServiceModeSelectorProps } from '../types';

const UnavailableBadge = () => {
  return (
    <View style={styles.unavailableBadgeContainer}>
      <Icon variant="errorFill" size={4} color={primitive.bk.$bbqBrown} />
      <Text style={styles.unavailableBadgeText}>Not Available</Text>
    </View>
  );
};

const ServiceModeList = ({
  serviceModeOptions,
  handleSelectServiceMode,
}: {
  serviceModeOptions: ServiceModeSelectorProps['serviceModeOptions'];
  handleSelectServiceMode: ServiceModeSelectorProps['handleSelectServiceMode'];
}) => {
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const memoizedServiceModes = useMemo(
    () =>
      serviceModeOptions?.map(serviceMode => (
        <Pressable
          key={serviceMode.name}
          onPress={() => handleSelectServiceMode(serviceMode.name)}
          style={{
            ...styles.serviceModeOption,
            backgroundColor: serviceMode.isAvailable ? 'white' : primitive.$blackOpacity10,
            ...(isDesktopWeb && {
              marginTop: 24,
            }),
          }}
          accessibilityRole="radio"
          accessibilityState={{ checked: serviceMode.isSelected }}
          accessibilityLabel={serviceMode.heading}
          disabled={!serviceMode.isAvailable}
        >
          <Image
            testID="service-mode-image"
            source={serviceMode.image}
            style={styles.serviceModeImage}
            accessibilityRole="image"
            accessibilityLabel={`${serviceMode.heading} image`}
          />
          <View style={styles.serviceModeContent}>
            <View style={styles.serviceModeHeader}>
              <IconStyled testID="service-mode-icon" variant={serviceMode.icon} />
              <View style={styles.serviceModeDetails}>
                <Text style={styles.serviceModeTitle}>{serviceMode.heading}</Text>
                <Text style={styles.serviceModeDescriptionText}>{serviceMode.description}</Text>
                {!serviceMode.isAvailable && <UnavailableBadge />}
              </View>
            </View>
          </View>
          {serviceMode.isSelected && <Icon testID="service-mode-check-mark" variant="checkMark" />}
        </Pressable>
      )),
    [serviceModeOptions, isDesktopWeb, handleSelectServiceMode]
  );

  return <ScrollView accessibilityRole="radiogroup">{memoizedServiceModes}</ScrollView>;
};

const Header = ({ onClose }: { onClose: () => void }) => {
  return (
    <View style={styles.headerContainer}>
      <View style={styles.headerSpace} />
      <Text style={styles.headerText}>{t('serviceModeSelectorTitle')}</Text>
      <CloseButton
        testID="service-mode-selector-close-button"
        _svg={{ size: '$5' }}
        onPress={onClose}
        borderRadius={0}
        accessibilityLabel="Close service mode selector"
      />
    </View>
  );
};

export const ServiceModeSelector = ({
  isOpen,
  onClose,
  serviceModeOptions,
  handleSelectServiceMode,
}: ServiceModeSelectorProps) => {
  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const eventMessage = 'Service mode selector';

  const content = (
    <>
      {!isDesktopWeb && <Header onClose={onClose} />}
      <ServiceModeList
        serviceModeOptions={serviceModeOptions}
        handleSelectServiceMode={handleSelectServiceMode}
      />
    </>
  );

  if (isDesktopWeb) {
    return (
      <InlineModal
        header={{
          title: t('serviceModeSelectorTitle'),
          variant: 'transparent',
        }}
        isOpen={isOpen}
        onClose={onClose}
        backgroundColor="white"
        webSize={{ height: 'auto', width: '40%' }}
        children={content}
        modalBodyStyle={{ padding: 0 }}
        modalContentStyle={styles.desktopWebModalContent}
      />
    );
  }

  return (
    <BottomSheet onOpenEventMessage={eventMessage} isOpen={isOpen} onClose={onClose}>
      <View style={styles.actionSheetContent}>{content}</View>
    </BottomSheet>
  );
};

const styles = StyleSheet.create({
  actionSheetContent: {
    padding: 0,
    alignItems: 'stretch',
  },
  headerContainer: {
    backgroundColor: primitive.bk.$white,
    paddingHorizontal: 8,
    paddingVertical: 12,
    display: 'flex',
    flexDirection: 'row',
  },
  headerSpace: {
    minWidth: 44,
  },
  headerText: {
    fontSize: 18,
    lineHeight: 18,
    fontFamily: primitive.fontFamily.$headers,
    textAlign: 'center',
    alignSelf: 'center',
    flex: 1,
    color: primitive.bk.$bbqBrown,
  },
  serviceModeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderColor: primitive.$blackOpacity10,
  },
  serviceModeContent: {
    flex: 1,
    marginLeft: 16,
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginRight: 16,
  },
  serviceModeDetails: {
    flexDirection: 'column',
  },
  serviceModeHeader: {
    flexDirection: 'row',
  },
  serviceModeTitle: {
    fontSize: 16,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
  serviceModeDescriptionText: {
    fontSize: 14,
    lineHeight: 30,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
  serviceModeImage: {
    width: 88,
    height: 88,
    borderRadius: 100,
  },
  unavailableBadgeContainer: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    alignItems: 'center',
    backgroundColor: primitive.$blackOpacity4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  unavailableBadgeText: {
    fontSize: primitive.fontSizes['2xs'],
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    marginLeft: 4,
  },
  desktopWebModalContent: {
    borderRadius: primitive.spacing.$5,
    paddingTop: 24,
    paddingBottom: 32,
    paddingHorizontal: 24,
    overflow: 'hidden',
  },
});
